'use client';

import { type ElementType } from 'react';

import { type VariantProps } from 'tailwind-variants';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { type ResponsiveOption } from '../../types/responsiveOption';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';

type Variants = VariantProps<typeof typographyStyles>;

export type ColorValue =
  | 'inherit'
  | 'text-primary'
  | 'text-secondary'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'cyan'
  | 'blue'
  | 'sky'
  | 'violet'
  | 'fuchsia'
  | 'pink'
  | 'neutral'
  | 'static-white'
  | 'accent-primary'
  | 'accent-secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'disabled'
  | 'brand-primary-a'
  | 'brand-secondary-a'
  | 'brand-primary-b'
  | 'brand-secondary-b';

/**
 * @deprecated These colours are deprecated.
 * Replace `white` with `static-white`,
 * `primary` with `accent-primary`,
 * `secondary` with `accent-secondary`
 * `default` with `text-primary`
 */

export type DeprecatedColorValue =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'white';

interface TypographyBaseProps {
  /**
   * Determines the alignment of the text content.
   * @default "start"
   * @deprecated Use `className` with Tailwind utility classes instead.
   */
  align?: Variants['align'];
  /**
   * The text colour of the component.
   * @default 'inherit'
   *
   * The values `default`, `primary`, secondary` and `white` are deprecated.
   * Use `text-primary` instead of `default`, `accent-primary` instead of `primary`, `accent-secondary` instead of `secondary` and `static-white` instead of `white`.
   */
  color?: ColorValue | DeprecatedColorValue;

  /**
   * The wrapping behaviour of the text content.
   * @default false
   * @deprecated Use `className` with Tailwind utility classes instead.
   */
  noWrap?: Variants['noWrap'];
  /**
   * Defines the type styles and corresponding HTML tag.
   * @default "body1"
   */
  variant?: ResponsiveOption<Variants['variant']>;
}

const typographyClasses = {
  root: 'zds-typography-root',
} as const;

const typographyStyles = tv({
  base: [typographyClasses.root, 'm-0 font-primary'],
  variants: {
    align: {
      start: 'text-start',
      end: 'text-end',
      center: 'text-center',
      inherit: '[text-align:inherit]',
    },
    variant: {
      body1:
        'z--typography-body1-font-size z--typography-body1-line-height font-regular tracking-normal',
      body2:
        'z--typography-body2-font-size z--typography-body2-line-height font-regular tracking-normal',
      h1: 'z--typography-h1-font-size z--typography-h1-line-height font-bold tracking-normal',
      h2: 'z--typography-h2-font-size z--typography-h2-line-height font-bold tracking-normal',
      h3: 'z--typography-h3-font-size z--typography-h3-line-height font-bold tracking-normal',
      h4: 'z--typography-h4-font-size z--typography-h4-line-height font-bold tracking-normal',
      h5: 'z--typography-h5-font-size z--typography-h5-line-height font-bold tracking-normal',
      h6: 'z--typography-h6-font-size z--typography-h6-line-height font-bold tracking-normal',
      subtitle1:
        'z--typography-subtitle1-font-size z--typography-subtitle1-line-height font-bold tracking-normal',
      subtitle2:
        'z--typography-subtitle2-font-size z--typography-subtitle2-line-height font-bold tracking-normal',
      caption:
        'z--typography-caption-font-size z--typography-caption-line-height font-medium tracking-normal',
      inherit: `
        [font-size:inherit]
        leading-[inherit]
        [font-weight:inherit]
        tracking-[inherit]
      `,
    },
    xs: {
      body1:
        'xs:z--typography-body1-font-size xs:z--typography-body1-line-height xs:font-regular xs:tracking-normal',
      body2:
        'xs:z--typography-body2-font-size xs:z--typography-body2-line-height xs:font-regular xs:tracking-normal',
      h1: 'xs:z--typography-h1-font-size xs:z--typography-h1-line-height xs:font-bold xs:tracking-normal',
      h2: 'xs:z--typography-h2-font-size xs:z--typography-h2-line-height xs:font-bold xs:tracking-normal',
      h3: 'xs:z--typography-h3-font-size xs:z--typography-h3-line-height xs:font-bold xs:tracking-normal',
      h4: 'xs:z--typography-h4-font-size xs:z--typography-h4-line-height xs:font-bold xs:tracking-normal',
      h5: 'xs:z--typography-h5-font-size xs:z--typography-h5-line-height xs:font-bold xs:tracking-normal',
      h6: 'xs:z--typography-h6-font-size xs:z--typography-h6-line-height xs:font-bold xs:tracking-normal',
      subtitle1: `
        xs:z--typography-subtitle1-font-size xs:z--typography-subtitle1-line-height xs:font-bold xs:tracking-normal
      `,
      subtitle2: `
        xs:z--typography-subtitle2-font-size xs:z--typography-subtitle2-line-height xs:font-bold xs:tracking-normal
      `,
      caption: `
        xs:z--typography-caption-font-size xs:z--typography-caption-line-height xs:font-medium xs:tracking-normal
      `,
      inherit: `
        xs:[font-size:inherit]
        xs:leading-[inherit]
        xs:[font-weight:inherit]
        xs:tracking-[inherit]
      `,
    },
    sm: {
      body1:
        'sm:z--typography-body1-font-size sm:z--typography-body1-line-height sm:font-regular sm:tracking-normal',
      body2:
        'sm:z--typography-body2-font-size sm:z--typography-body2-line-height sm:font-regular sm:tracking-normal',
      h1: 'sm:z--typography-h1-font-size sm:z--typography-h1-line-height sm:font-bold sm:tracking-normal',
      h2: 'sm:z--typography-h2-font-size sm:z--typography-h2-line-height sm:font-bold sm:tracking-normal',
      h3: 'sm:z--typography-h3-font-size sm:z--typography-h3-line-height sm:font-bold sm:tracking-normal',
      h4: 'sm:z--typography-h4-font-size sm:z--typography-h4-line-height sm:font-bold sm:tracking-normal',
      h5: 'sm:z--typography-h5-font-size sm:z--typography-h5-line-height sm:font-bold sm:tracking-normal',
      h6: 'sm:z--typography-h6-font-size sm:z--typography-h6-line-height sm:font-bold sm:tracking-normal',
      subtitle1: `
        sm:z--typography-subtitle1-font-size sm:z--typography-subtitle1-line-height sm:font-bold sm:tracking-normal
      `,
      subtitle2: `
        sm:z--typography-subtitle2-font-size sm:z--typography-subtitle2-line-height sm:font-bold sm:tracking-normal
      `,
      caption: `
        sm:z--typography-caption-font-size sm:z--typography-caption-line-height sm:font-medium sm:tracking-normal
      `,
      inherit: `
        sm:[font-size:inherit]
        sm:leading-[inherit]
        sm:[font-weight:inherit]
        sm:tracking-[inherit]
      `,
    },
    md: {
      body1:
        'md:z--typography-body1-font-size md:z--typography-body1-line-height md:font-regular md:tracking-normal',
      body2:
        'md:z--typography-body2-font-size md:z--typography-body2-line-height md:font-regular md:tracking-normal',
      h1: 'md:z--typography-h1-font-size md:z--typography-h1-line-height md:font-bold md:tracking-normal',
      h2: 'md:z--typography-h2-font-size md:z--typography-h2-line-height md:font-bold md:tracking-normal',
      h3: 'md:z--typography-h3-font-size md:z--typography-h3-line-height md:font-bold md:tracking-normal',
      h4: 'md:z--typography-h4-font-size md:z--typography-h4-line-height md:font-bold md:tracking-normal',
      h5: 'md:z--typography-h5-font-size md:z--typography-h5-line-height md:font-bold md:tracking-normal',
      h6: 'md:z--typography-h6-font-size md:z--typography-h6-line-height md:font-bold md:tracking-normal',
      subtitle1: `
        md:z--typography-subtitle1-font-size md:z--typography-subtitle1-line-height md:font-bold md:tracking-normal
      `,
      subtitle2: `
        md:z--typography-subtitle2-font-size md:z--typography-subtitle2-line-height md:font-bold md:tracking-normal
      `,
      caption: `
        md:z--typography-caption-font-size md:z--typography-caption-line-height md:font-medium md:tracking-normal
      `,
      inherit: `
        md:[font-size:inherit]
        md:leading-[inherit]
        md:[font-weight:inherit]
        md:tracking-[inherit]
      `,
    },
    lg: {
      body1:
        'lg:z--typography-body1-font-size lg:z--typography-body1-line-height lg:font-regular lg:tracking-normal',
      body2:
        'lg:z--typography-body2-font-size lg:z--typography-body2-line-height lg:font-regular lg:tracking-normal',
      h1: 'lg:z--typography-h1-font-size lg:z--typography-h1-line-height lg:font-bold lg:tracking-normal',
      h2: 'lg:z--typography-h2-font-size lg:z--typography-h2-line-height lg:font-bold lg:tracking-normal',
      h3: 'lg:z--typography-h3-font-size lg:z--typography-h3-line-height lg:font-bold lg:tracking-normal',
      h4: 'lg:z--typography-h4-font-size lg:z--typography-h4-line-height lg:font-bold lg:tracking-normal',
      h5: 'lg:z--typography-h5-font-size lg:z--typography-h5-line-height lg:font-bold lg:tracking-normal',
      h6: 'lg:z--typography-h6-font-size lg:z--typography-h6-line-height lg:font-bold lg:tracking-normal',
      subtitle1: `
        lg:z--typography-subtitle1-font-size lg:z--typography-subtitle1-line-height lg:font-bold lg:tracking-normal
      `,
      subtitle2: `
        lg:z--typography-subtitle2-font-size lg:z--typography-subtitle2-line-height lg:font-bold lg:tracking-normal
      `,
      caption: `
        lg:z--typography-caption-font-size lg:z--typography-caption-line-height lg:font-medium lg:tracking-normal
      `,
      inherit: `
        lg:[font-size:inherit]
        lg:leading-[inherit]
        lg:[font-weight:inherit]
        lg:tracking-[inherit]
      `,
    },
    xl: {
      body1:
        'xl:z--typography-body1-font-size xl:z--typography-body1-line-height xl:font-regular xl:tracking-normal',
      body2:
        'xl:z--typography-body2-font-size xl:z--typography-body2-line-height xl:font-regular xl:tracking-normal',
      h1: 'xl:z--typography-h1-font-size xl:z--typography-h1-line-height xl:font-bold xl:tracking-normal',
      h2: 'xl:z--typography-h2-font-size xl:z--typography-h2-line-height xl:font-bold xl:tracking-normal',
      h3: 'xl:z--typography-h3-font-size xl:z--typography-h3-line-height xl:font-bold xl:tracking-normal',
      h4: 'xl:z--typography-h4-font-size xl:z--typography-h4-line-height xl:font-bold xl:tracking-normal',
      h5: 'xl:z--typography-h5-font-size xl:z--typography-h5-line-height xl:font-bold xl:tracking-normal',
      h6: 'xl:z--typography-h6-font-size xl:z--typography-h6-line-height xl:font-bold xl:tracking-normal',
      subtitle1: `
        xl:z--typography-subtitle1-font-size xl:z--typography-subtitle1-line-height xl:font-bold xl:tracking-normal
      `,
      subtitle2: `
        xl:z--typography-subtitle2-font-size xl:z--typography-subtitle2-line-height xl:font-bold xl:tracking-normal
      `,
      caption: `
        xl:z--typography-caption-font-size xl:z--typography-caption-line-height xl:font-medium xl:tracking-normal
      `,
      inherit: `
        xl:[font-size:inherit]
        xl:leading-[inherit]
        xl:[font-weight:inherit]
        xl:tracking-[inherit]
      `,
    },
    color: {
      // semantic colours
      inherit: 'text-inherit',
      'text-primary': 'text-primary',
      'text-secondary': 'text-secondary',
      'accent-primary': 'text-accent-primary-main',
      'accent-secondary': 'text-accent-secondary-main',
      success: 'text-success-main',
      error: 'text-error-main',
      warning: 'text-warning-main',
      info: 'text-info-main',
      disabled: 'disabled-foreground',

      // deprecated colours
      default: 'text-primary',
      primary: 'text-accent-primary-main',
      secondary: 'text-accent-secondary-main',
      white: 'text-static-white-main',

      // primitive colours
      red: 'text-red-main',
      orange: 'text-orange-main',
      yellow: 'text-yellow-main',
      lime: 'text-lime-main',
      green: 'text-green-main',
      emerald: 'text-emerald-main',
      cyan: 'text-cyan-main',
      blue: 'text-blue-main',
      sky: 'text-sky-main',
      violet: 'text-violet-main',
      fuchsia: 'text-fuchsia-main',
      pink: 'text-pink-main',
      neutral: 'text-neutral-strong-1',
      'static-white': 'text-static-white-main',

      // brand colours
      'brand-primary-a': 'text-brand-primary-a-main',
      'brand-secondary-a': 'text-brand-secondary-a-main',
      'brand-primary-b': 'text-brand-primary-b-main',
      'brand-secondary-b': 'text-brand-primary-b-main',
    },
    noWrap: {
      true: 'truncate',
    },
  },
});

const variantToAs = (variant: Variants['variant'] = 'body1'): ElementType => {
  switch (variant) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      return variant;
    case 'subtitle1':
    case 'subtitle2':
      return 'h6';
    case 'caption':
      return 'span';
    case 'body1':
    case 'body2':
    case 'inherit':
    default:
      return 'p';
  }
};

type TypographyProps<RootComponent extends ElementType = 'p'> =
  PolymorphicComponentProps<TypographyBaseProps, RootComponent>;

type TypographyComponent = PolymorphicComponent<TypographyBaseProps, 'p'>;

const Typography: TypographyComponent = (props) => {
  const {
    component,
    align,
    variant = 'body1',
    color = 'inherit',
    noWrap,
    className,
    children,
    ref,
    ...rest
  } = props;

  const { defaultOption, responsiveOption } = resolveResponsiveProps(variant);

  const Root = component || variantToAs(defaultOption);

  // can be removed once deprecated colours are retired
  const resolveColor = (
    colorValue: ColorValue | DeprecatedColorValue
  ): ColorValue => {
    if (colorValue === 'default') {
      return 'text-primary';
    }
    if (colorValue === 'white') {
      return 'static-white';
    }
    if (colorValue === 'primary') {
      return 'accent-primary';
    }

    if (colorValue === 'secondary') {
      return 'accent-secondary';
    }
    return colorValue;
  };

  const styles = typographyStyles({
    align,
    variant: defaultOption,
    color: resolveColor(color),
    noWrap,
    xs: responsiveOption?.xs,
    sm: responsiveOption?.sm,
    md: responsiveOption?.md,
    lg: responsiveOption?.lg,
    xl: responsiveOption?.xl,
    className,
  });

  return (
    <Root {...rest} ref={ref} className={styles}>
      {children}
    </Root>
  );
};

Typography.displayName = 'Typography';

export {
  Typography,
  type TypographyProps,
  type TypographyBaseProps,
  typographyClasses,
};
